import { Field } from 'formik';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';

export const StyledRadioWrapper = styled.div`
	position: relative;
	padding: 1rem;

	${({ disabled }) =>
		disabled &&
		css`
			opacity: 0.5;
			pointer-events: none;
		`};

	input {
		display: none;
	}
`;

export const StyledRadio = styled.div`
	position: absolute;
	left: 0;
	top: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 2.4rem;
	height: 2.4rem;
	border-radius: 100%;
	transform: translateY(-50%) scale(0.8);
	border: 1px solid ${({ theme }) => theme.colors.gray};
	transition: background-color 150ms, transform 150ms cubic-bezier(0.92, -0.01, 0, 1.46);

	:after {
		content: '';
		width: 0.8rem;
		height: 0.8rem;
		border-radius: 100%;
		background-color: ${({ theme }) => theme.colors.borderColor};
		opacity: 0;
		transform: scale(0.9);
		transition: opacity 150ms, background-color 150ms, transform 150ms;
	}

	&:focus {
		transform: translateY(-50%) scale(1);
		border: 1px solid ${({ theme }) => theme.colors.grayLigh};
	}

	${({ checked }) =>
		checked &&
		css`
			background-color: ${({ theme }) => theme.colors.secondary};
			transform: translateY(-50%) scale(1);

			&:after {
				opacity: 1;
				background-color: #fff;
				transform: scale(1);
			}

			&:focus {
				transform: translateY(-50%) scale(1.2);
				border: 1px solid ${({ theme }) => theme.colors.grayLighter};
			}
		`};
`;

export const StyledRadioLabel = styled.label`
	position: relative;
	font-size: 1.6rem;
	color: ${({ theme }) => theme.colors.black};
	padding-left: 3.4rem;
	cursor: pointer;
	display: block;

	&:hover {
		${StyledRadio}:after {
			opacity: 1;
		}
	}
`;

interface Props {
	id: string;
	name: string;
	label: string | React.ReactNode;
	setFieldValue: (x: string, y: string) => void;
	disabled?: boolean;
}

const Radio = ({
	field: { name, value, onChange, onBlur },
	setFieldValue,
	id,
	label,
	className,
	disabled,
	...props
}) => {
	const filterOnKeyDowns = e => {
		if (e.keyCode === 32) {
			e.preventDefault();
			setFieldValue(name, id);
		}
	};

	return (
		<StyledRadioWrapper disabled={disabled}>
			<input
				name={name}
				id={id}
				type="radio"
				value={id}
				checked={id === value}
				onChange={onChange}
				onBlur={onBlur}
				{...props}
			/>

			<StyledRadioLabel htmlFor={id}>
				<StyledRadio checked={id === value} tabIndex="0" id={id} onKeyDown={filterOnKeyDowns} />
				{label}
			</StyledRadioLabel>
		</StyledRadioWrapper>
	);
};

const FormikRadio: FC<Props> = props => <Field component={Radio} {...props} />;

export default FormikRadio;
